import {
  AddressWithCountry,
  ConsumptionPointContractInfo,
  Customer,
  ProductionPointContractInfo,
  ProductionPointPowerStationInfo,
} from "@nano-portal/customers"
import { HouseholdContactFormVales } from "../contact/householdContactFormController"
import { BusinessContactFormVales } from "../contact/businessContactForm.utils"
import { CustomerType } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { ContractTypeFormValues } from "../contractType/contractTypeForm.utils"
import { ConsumptionPointFormValues } from "../consumptionPoint/consumptionPointForm.utils"
import {
  getPowerStationContractTermination,
  ProductionPointFormValues,
} from "../productionPoint/productionPointForm.utils"
import {
  PaymentFormValues,
  hasConsumptionBankAccount,
  hasProductionAndConsumptionBankAccounts,
  hasProductionBankAccount,
} from "../payment/paymentForm.utils"
import { getConsumptionPointAddress } from "../acquisitionForm.utils"
import { formatEanWithoutPrefix } from "../../acquisitionForm/format"
import { assertUnreachable } from "../../../../../utils/types"

export function getContactInfo(args: {
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
}) {
  if (args.householdContactFormValues) {
    const { householdContactFormValues } = args

    const address: AddressWithCountry = {
      city: householdContactFormValues.addressCity,
      street: householdContactFormValues.addressStreet,
      descriptiveNumber: householdContactFormValues.addressDescriptiveNumber,
      orientationNumber: householdContactFormValues.addressOrientationNumber,
      countryCode: householdContactFormValues.addressCountryCode,
      postalCode: householdContactFormValues.addressPostalCode,
    }

    return {
      address,
      email: householdContactFormValues.email,
      firstName: householdContactFormValues.firstName,
      lastName: householdContactFormValues.lastName,
      birthDate: new Date(
        householdContactFormValues.birthYear,
        householdContactFormValues.birthMonth,
        householdContactFormValues.birthDate
      ),
      phone: householdContactFormValues.phone,
      contactAddress: householdContactFormValues.contactAddress,
      deliveryPointAddress: householdContactFormValues.deliveryPointAddress,
      customerTypeData: {
        customerType: "household" as const,
      },
    }
  }

  if (args.businessContactFormValues) {
    const { businessContactFormValues } = args

    const address: AddressWithCountry = {
      city: businessContactFormValues.addressCity,
      street: businessContactFormValues.addressStreet,
      descriptiveNumber: businessContactFormValues.addressDescriptiveNumber,
      orientationNumber: businessContactFormValues.addressOrientationNumber,
      countryCode: businessContactFormValues.addressCountryCode,
      postalCode: businessContactFormValues.addressPostalCode,
    }

    return {
      address,
      email: businessContactFormValues.contactEmail,
      firstName: businessContactFormValues.contactFirstName,
      lastName: businessContactFormValues.contactLastName,
      birthDate: new Date(
        businessContactFormValues.contactBirthYear,
        businessContactFormValues.contactBirthMonth,
        businessContactFormValues.contactBirthDate
      ),
      phone: businessContactFormValues.contactPhone,
      contactAddress: businessContactFormValues.contactAddress,
      deliveryPointAddress: businessContactFormValues.deliveryPointAddress,
      customerTypeData: {
        customerType: "business" as const,
        companyName: businessContactFormValues.companyName,
        registrationNumber: businessContactFormValues.registrationNumber,
        taxNumber: !!businessContactFormValues.taxNumber ? businessContactFormValues.taxNumber : undefined,
      },
    }
  }

  // won't happen, since we redirected the user before
  throw new Error("No contact info")
}

type GetProductDataArgs = {
  customerType: CustomerType
  contractTypeFormValues: ContractTypeFormValues
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
  consumptionPointFormValues?: ConsumptionPointFormValues
  productionPointFormValues?: ProductionPointFormValues
  paymentFormValues: PaymentFormValues
}

function getConsumptionProductData(
  args: Pick<
    GetProductDataArgs,
    "consumptionPointFormValues" | "paymentFormValues" | "householdContactFormValues" | "businessContactFormValues"
  >
): Customer["productData"] {
  const address = getConsumptionPointAddress(args)
  const lastBillingPath = args.consumptionPointFormValues?.lastBilling.bucketPath
  if (!args.consumptionPointFormValues || !lastBillingPath) {
    throw new Error("Consumption point form values are missing")
  }

  if (!hasConsumptionBankAccount(args.paymentFormValues)) {
    throw new Error("Consumption bank account is missing")
  }

  return {
    product: "CONSUMPTION",
    consumptionPoint: {
      ean: formatEanWithoutPrefix(args.consumptionPointFormValues.ean),
      address,
      circuitBreaker: {
        capacity: args.consumptionPointFormValues.circuitBreakerCapacity,
        phases: args.consumptionPointFormValues.circuitBreakerPhases,
      },
      currentSupplier: args.consumptionPointFormValues.currentSupplier,
      distributionArea: args.consumptionPointFormValues.distributionArea,
      distributionTariff: args.consumptionPointFormValues.distributionTariff,
      highTariffConsumptionKWh: args.consumptionPointFormValues.highTariffConsumptionKWh,
      lowTariffConsumptionKWh: args.consumptionPointFormValues.lowTariffConsumptionKWh ?? undefined,
      currentContractInfo: undefined,
      contractInfo: getConsumptionPointContractInfo(args.consumptionPointFormValues),
      paymentMethod: args.paymentFormValues.advancePaymentMethod,
      bankAccount: args.paymentFormValues.advancePaymentBankAccount,
      lastBillingPath,
    },
  }
}

function getProductionProductData(
  args: Pick<
    GetProductDataArgs,
    | "businessContactFormValues"
    | "householdContactFormValues"
    | "productionPointFormValues"
    | "paymentFormValues"
    | "contractTypeFormValues"
  >
): Customer["productData"] {
  const address = getConsumptionPointAddress(args)
  const powerStationInfo = args.productionPointFormValues?.powerStationInfo

  const contractTermination = powerStationInfo ? getPowerStationContractTermination(powerStationInfo) : undefined

  const terminationContractFilledAndNotUploaded = contractTermination && !contractTermination.bucketPath
  const connectionContractPath = args.productionPointFormValues?.connectionContract.bucketPath
  if (!args.productionPointFormValues || !connectionContractPath || terminationContractFilledAndNotUploaded) {
    throw new Error("Production point form values are missing")
  }

  if (!hasProductionBankAccount(args.paymentFormValues)) {
    throw new Error("Production bank account is missing")
  }

  return {
    product: "PRODUCTION",
    productionPoint: {
      address,
      // will be present
      ean: formatEanWithoutPrefix(args.productionPointFormValues.ean ?? ""),
      type: args.productionPointFormValues.powerStationInfo.powerStationType,
      name: args.productionPointFormValues.name,
      invoicingMethod: args.productionPointFormValues.invoicingMethod,
      bankAccount: args.paymentFormValues.productionPaymentBankAccount,
      powerKwp: args.productionPointFormValues.powerKwp,
      productionDataTariff: args.contractTypeFormValues.productionDataTariff,
      powerStation: getProductionPointPowerStationInfo(args.productionPointFormValues),
      connectionContractPath,
    },
  }
}

function getProductionAndConsumptionProductData(args: GetProductDataArgs): Customer["productData"] {
  const address = getConsumptionPointAddress(args)

  const powerStationInfo = args.productionPointFormValues?.powerStationInfo

  const powerStationContractTermination = powerStationInfo
    ? getPowerStationContractTermination(powerStationInfo)
    : undefined
  const terminationContractFilledAndNotUploaded =
    powerStationContractTermination && !powerStationContractTermination.bucketPath

  const connectionContractPath = args.productionPointFormValues?.connectionContract.bucketPath
  const lastBillingPath = args.consumptionPointFormValues?.lastBilling.bucketPath

  if (!args.productionPointFormValues || !args.consumptionPointFormValues) {
    throw new Error("Production or consumption point form values are missing")
  }

  if (!hasConsumptionBankAccount(args.paymentFormValues)) {
    throw new Error("Consumption bank account is missing")
  }

  if (!lastBillingPath || !connectionContractPath || terminationContractFilledAndNotUploaded) {
    throw new Error("Requiered documents were not uploaded")
  }

  return {
    product: "PRODUCTION_AND_CONSUMPTION",
    consumptionPoint: {
      ean: formatEanWithoutPrefix(args.consumptionPointFormValues.ean),
      address,
      circuitBreaker: {
        capacity: args.consumptionPointFormValues.circuitBreakerCapacity,
        phases: args.consumptionPointFormValues.circuitBreakerPhases,
      },
      currentSupplier: args.consumptionPointFormValues.currentSupplier,
      distributionArea: args.consumptionPointFormValues.distributionArea,
      distributionTariff: args.consumptionPointFormValues.distributionTariff,
      highTariffConsumptionKWh: args.consumptionPointFormValues.highTariffConsumptionKWh,
      lowTariffConsumptionKWh: args.consumptionPointFormValues.lowTariffConsumptionKWh ?? undefined,
      contractInfo: getConsumptionPointContractInfo(args.consumptionPointFormValues),
      currentContractInfo: undefined,
      paymentMethod: args.paymentFormValues.advancePaymentMethod,
      bankAccount: args.paymentFormValues.advancePaymentBankAccount,
      lastBillingPath,
    },
    productionPoint: {
      address: getConsumptionPointAddress(args),
      ean: formatEanWithoutPrefix(args.productionPointFormValues.ean ?? args.consumptionPointFormValues.ean),
      type: args.productionPointFormValues.powerStationInfo.powerStationType,
      name: args.productionPointFormValues.name,
      invoicingMethod: args.productionPointFormValues.invoicingMethod,
      powerStation: getProductionPointPowerStationInfo(args.productionPointFormValues, args.consumptionPointFormValues),

      bankAccount: hasProductionAndConsumptionBankAccounts(args.paymentFormValues)
        ? args.paymentFormValues.productionPaymentBankAccount ?? args.paymentFormValues.advancePaymentBankAccount
        : args.paymentFormValues.advancePaymentBankAccount,
      powerKwp: args.productionPointFormValues.powerKwp,
      productionDataTariff: args.contractTypeFormValues.productionDataTariff,
      connectionContractPath,
    },
  }
}

export function getProductData(args: GetProductDataArgs): Customer["productData"] {
  const product = args.contractTypeFormValues.product

  switch (product) {
    case "consumption":
      return getConsumptionProductData(args)
    case "production":
      return getProductionProductData(args)
    case "productionAndConsumption":
      return getProductionAndConsumptionProductData(args)
    default:
      return assertUnreachable(product)
  }
}

function getConsumptionPointContractInfo(formValues: ConsumptionPointFormValues): ConsumptionPointContractInfo {
  if (formValues.contractInfo.terminationOption === "TERMINATE") {
    return {
      shouldTerminateContract: true,
      currentContractInfo:
        formValues.contractInfo.currentContract.contractType === "FIXED_TERM"
          ? {
              contractType: "FIXED_TERM",
              contractEndDate: new Date(
                formValues.contractInfo.currentContract.contractEndYear,
                formValues.contractInfo.currentContract.contractEndMonth,
                formValues.contractInfo.currentContract.contractEndDate
              ),
              terminationNoticeInDaysBeforeEnd:
                formValues.contractInfo.currentContract.terminationNoticeInDaysBeforeEnd,
            }
          : {
              contractType: "VARIABLE_TERM",
              noticePeriod: formValues.contractInfo.currentContract.noticePeriod,
            },
    }
  }

  return {
    shouldTerminateContract: false,
    contractEndDate: new Date(
      formValues.contractInfo.contractEndYear,
      formValues.contractInfo.contractEndMonth,
      formValues.contractInfo.contractEndDate
    ),
    contractTerminationPath: formValues.contractInfo.contractTermination?.bucketPath,
  }
}

function getProductionPointPowerStationInfo(
  formValues: ProductionPointFormValues,
  consumptionFormValues?: ConsumptionPointFormValues
): ProductionPointPowerStationInfo {
  if (formValues.powerStationInfo.powerStationType === "NEW") {
    return {
      type: "NEW",
    }
  }

  let contractInfo: ProductionPointContractInfo
  if (!formValues.powerStationInfo.contractInfo) {
    if (!consumptionFormValues) {
      throw new Error("Neither power station contract info nor consumption contract info are filled")
    }

    const consumptionContract = getConsumptionPointContractInfo(consumptionFormValues)
    if (consumptionContract.shouldTerminateContract === false) {
      contractInfo = {
        shouldTerminateContract: false,
        contractEndDate: consumptionContract.contractEndDate,
        contractTerminationPath: consumptionContract.contractTerminationPath,
      }

      return {
        type: "EXISTING",
        contractInfo,
      }
    }

    contractInfo = {
      shouldTerminateContract: true,
      currentSupplier: consumptionFormValues.currentSupplier,
      currentContractInfo: consumptionContract.currentContractInfo,
    }

    return {
      type: "EXISTING",
      contractInfo,
    }
  }

  if (formValues.powerStationInfo.contractInfo.terminationOption === "TERMINATE") {
    contractInfo = {
      shouldTerminateContract: true,
      currentSupplier: formValues.powerStationInfo.contractInfo.currentSupplier,
      currentContractInfo:
        formValues.powerStationInfo.contractInfo.currentContract.contractType === "FIXED_TERM"
          ? {
              contractType: "FIXED_TERM",
              contractEndDate: new Date(
                formValues.powerStationInfo.contractInfo.currentContract.contractEndYear,
                formValues.powerStationInfo.contractInfo.currentContract.contractEndMonth,
                formValues.powerStationInfo.contractInfo.currentContract.contractEndDate
              ),
              terminationNoticeInDaysBeforeEnd:
                formValues.powerStationInfo.contractInfo.currentContract.terminationNoticeInDaysBeforeEnd,
            }
          : {
              contractType: "VARIABLE_TERM",
              noticePeriod: formValues.powerStationInfo.contractInfo.currentContract.noticePeriod,
            },
    }

    return {
      type: "EXISTING",
      contractInfo,
    }
  }

  contractInfo = {
    shouldTerminateContract: false,
    contractEndDate: new Date(
      formValues.powerStationInfo.contractInfo.contractEndYear,
      formValues.powerStationInfo.contractInfo.contractEndMonth,
      formValues.powerStationInfo.contractInfo.contractEndDate
    ),
    contractTerminationPath: formValues.powerStationInfo.contractInfo.contractTermination?.bucketPath,
  }

  return {
    type: "EXISTING",
    contractInfo,
  }
}
